import { Link, useLocation } from "react-router-dom";
import { Bell, Crown, LogOut } from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarHeader,
  SidebarHideWhenCollapsed, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarProvider, SidebarTrigger, useSidebar,
} from "@/shared/ui/sidebar.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Avatar, AvatarFallback } from "@/shared/ui/avatar.jsx";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { initials } from "@/shared/lib/format.js";
import { MASTER_NAV, isActiveRoute } from "./navigation.js";

/**
 * Moldura do painel Master. Mesma estrutura do `AppShell`, com o menu e a
 * identidade visual do ambiente administrativo.
 */
export function MasterShell({ title, subtitle, actions, children }) {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <MasterSidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl">
            <SidebarTrigger />

            <div className="hidden min-w-0 flex-col md:flex">
              <div className="flex items-center gap-2">
                <h1 className="truncate font-display text-base font-semibold leading-tight">
                  {title}
                </h1>
                <Badge className="h-5 gap-1 bg-gradient-to-r from-warning to-primary text-white">
                  <Crown className="h-3 w-3" /> Master
                </Badge>
              </div>
              {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {actions}
              <Button variant="ghost" size="icon" className="relative" aria-label="Notificações">
                <Bell className="h-4 w-4" />
                <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-primary" />
              </Button>
            </div>
          </header>

          <main className="min-w-0 flex-1">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function MasterSidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { collapsed, setMobileOpen } = useSidebar();

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2.5 px-1.5 py-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-warning to-primary shadow-lg shadow-primary/20">
            <Crown className="h-5 w-5 text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-semibold leading-tight">OdontoPlug</p>
              <p className="truncate text-[10px] font-medium uppercase tracking-widest text-warning">
                Master
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {MASTER_NAV.map((grupo) => (
          <SidebarGroup key={grupo.label}>
            <SidebarMenu>
              {grupo.items.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActiveRoute(pathname, item.url)}
                    tooltip={item.title}
                  >
                    <Link to={item.url} onClick={() => setMobileOpen(false)}>
                      <item.icon />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarHideWhenCollapsed className="mb-2 flex items-center gap-3 px-1">
          <Avatar className="h-9 w-9 border border-sidebar-border/60">
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials(user?.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-none">{user?.fullName}</p>
            <p className="mt-1 truncate text-[11px] text-muted-foreground">{user?.email}</p>
          </div>
        </SidebarHideWhenCollapsed>

        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={logout}
              tooltip="Sair da conta"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut />
              <span>Sair da conta</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
